// Colombian currency formatting
export function formatCOP(value) {
  if (value === null || value === undefined || value === 0) return "N/D";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Short value format (e.g., $1.450 M)
export function formatCOPShort(value) {
  if (!value || value === 0) return "N/D";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)} MM`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)} M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)} K`;
  return `$${value}`;
}

// Date formatting
export function formatDate(dateStr, opts = {}) {
  if (!dateStr) return "N/D";
  const date = new Date(dateStr);
  if (isNaN(date)) return "N/D";
  return date.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...opts,
  });
}

// Relative time
export function formatRelative(dateStr) {
  if (!dateStr) return "N/D";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = date - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (diff < 0) return "Vencido";
  if (days === 0) return "Hoy";
  if (days === 1) return "Mañana";
  if (days < 7) return `${days} días`;
  if (days < 30) return `${Math.floor(days / 7)} sem`;
  return formatDate(dateStr);
}

// Countdown
export function getCountdown(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diff = date - now;
  if (diff <= 0) return { expired: true };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { days, hours, minutes, expired: false };
}

// Score category display
export function getScoreBadge(category, score) {
  const map = {
    FIRE: { label: "🔥 ALTA PROBABILIDAD", cls: "badge-fire" },
    GREEN: { label: "🟢 VIABLE", cls: "badge-green" },
    YELLOW: { label: "🟡 EVALUAR", cls: "badge-yellow" },
    WHITE: { label: "⚪ BAJA PRIORIDAD", cls: "badge-white" },
  };
  return map[category] || { label: `Score ${score}`, cls: "badge-white" };
}

// RUP status
export function getRupBadge(status) {
  const map = {
    CONFIRMADO: { label: "🟢 CONFIRMADO", cls: "state-confirmed" },
    INFERIDO: { label: "🟡 INFERIDO", cls: "state-inferred" },
    NO_CUMPLE: { label: "🔴 NO CUMPLE", cls: "state-no-comply" },
    NO_VERIFICADO: { label: "⚪ NO VERIFICADO", cls: "state-unverified" },
    RUP_SI: { label: "✅ RUP SÍ", cls: "state-confirmed" },
    RUP_NO: { label: "❌ RUP NO", cls: "state-no-comply" },
    RUP_NO_VERIFICADO: { label: "⚪ RUP NO VERIFICADO", cls: "state-unverified" },
    RUP_NO_APLICA: { label: "➖ RUP NO APLICA", cls: "state-inferred" },
  };
  return map[status] || map["NO_VERIFICADO"];
}

// AI priority badge
export function getAIPriorityBadge(priority) {
  const map = {
    ALTA_PRIORIDAD: {
      label: "ALTA PRIORIDAD",
      cls: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
      icon: "IA",
      dot: "bg-emerald-400",
    },
    REVISAR: {
      label: "REVISAR",
      cls: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
      icon: "IA",
      dot: "bg-amber-400",
    },
    NO_RECOMENDADO: {
      label: "NO RECOMENDADO",
      cls: "bg-slate-600/30 text-slate-400 border border-slate-600/30",
      icon: "IA",
      dot: "bg-slate-500",
    },
  };
  return map[priority] || null;
}

// RUP requirement based on contract modality (rule-based, no AI cost)
// Returns: 'NO_APLICA' | 'REQUERIDO' | null (desconocido)
export function requiresRup(modality) {
  if (!modality) return null;
  const m = modality
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // elimina tildes
  if (m.includes("minima cuantia") || m.includes("contratacion directa")) {
    return "NO_APLICA";
  }
  if (
    m.includes("licitacion") ||
    m.includes("seleccion abreviada") ||
    m.includes("concurso de meritos") ||
    m.includes("asociacion publico privada")
  ) {
    return "REQUERIDO";
  }
  return null;
}

// Urgency color based on days to close
export function getUrgencyColor(dateStr) {
  if (!dateStr) return "text-slate-400";
  const days = Math.floor((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return "text-slate-500";
  if (days <= 3) return "text-red-400";
  if (days <= 7) return "text-amber-400";
  if (days <= 14) return "text-yellow-400";
  return "text-slate-400";
}
